import { createRouter, createWebHistory } from 'vue-router';
import EventBus from '@/bus';
import store from '@/store';
import * as types from '@/store/mutationTypes';
import {
  APPEARANCE_SET_PREFERENCES_VIEW,
  APPEARANCE_SET_IS_PUBLIC_VIEW_MODE,
  AUTHORISATION_SET_VIEW_TYPE
} from '@/store/mutationTypes';
import { VIEW_TYPES, ROLES } from '@/constants';
import isPagePathVisible from './isPagePathVisible';

// Lazy load components - only load them when the route is visited.
const LoginCallback = () => import('@/components/views/LoginCallback');
const Dashboard = () => import('@/components/views/dashboard/Dashboard');
const Page = () => import('@/components/views/Page');
// TODO: Re-enable feedback portal here and in App.vue in CSPOL-624
// const FeedbackPortal = () => import('@/components/views/FeedbackPortal');
const TempPage = () => import('@/components/views/TempPage');
const Context = () => import('@/components/views/preferences/Context');
const ContentAdmin = () => import('@/components/views/preferences/ContentAdmin');
const GroupAdmin = () => import('@/components/views/preferences/GroupAdmin');
const AuditLog = () => import('@/components/views/preferences/AuditLog');
const ReportDashboard = () => import('@/components/views/preferences/ReportDashboard');
const AssuranceIntegration = () => import('@/components/views/preferences/AssuranceIntegration');
const ContentRegister = () => import('@/components/views/preferences/ContentRegister');

export const routes = [
  {
    path: '/login/callback/idp',
    name: 'LoginCallback',
    component: LoginCallback
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { permission: 1 }
  },
  {
    path: '/module/:moduleId/page/:pagePath',
    name: 'Page',
    component: Page,
    meta: { permission: 1 }
  },
  {
    path: '/tempmodule/:moduleId/temppage/:pagePath',
    name: 'TempPage',
    component: TempPage,
    meta: { permission: 1 }
  },
  {
    path: '/preferences/context',
    name: 'Context',
    component: Context,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/contentAdmin',
    name: 'ContentAdmin',
    component: ContentAdmin,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/groupAdmin',
    name: 'GroupAdmin',
    component: GroupAdmin,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/auditLog',
    name: 'AuditLog',
    component: AuditLog,
    meta: { permission: 2 }
  },
  // TODO: Re-enable feedback portal here and in App.vue in CSPOL-624
  // {
  //   path: '/feedback',
  //   name: 'FeedbackPortal',
  //   component: FeedbackPortal,
  //   meta: { permission: 1 }
  // },
  {
    path: '/preferences/reportDashboard',
    name: 'ReportDashboard',
    component: ReportDashboard,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/assuranceIntegration',
    name: 'AssuranceIntegration',
    component: AssuranceIntegration,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/contentRegister',
    name: 'ContentRegister',
    component: ContentRegister,
    meta: { permission: 2 }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
    meta: { permission: 0 }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// For each route redirect, check if the `to` route requires authentication.
// If the user is not logged in and the route requires authentication then redirect
// to login screen.

const beforeEachRoute = async (to, _from, next) => {
  store.commit(APPEARANCE_SET_IS_PUBLIC_VIEW_MODE, false);

  document.documentElement.scrollTop = 0;
  if (store.state.session.viewType === VIEW_TYPES.CLIENT) {
    const isPathVisible = isPagePathVisible(
      to.params.moduleId,
      to.params.pagePath,
      store.state.pageVisibilities.pages
    );

    // TODO: Investigate on this, it probably not be used anymore.
    if (!isPathVisible) {
      EventBus.$emit('notVisible');
      return;
    }
  }

  // Change the side nav view dependent on if preferences are need to be shown.
  if (
    [
      'Settings',
      'Context',
      'ContentAdmin',
      'GroupAdmin',
      'AuditLog',
      'ReportDashboard',
      'AssuranceIntegration',
      'ContentRegister'
    ].includes(to.name)
  ) {
    store.commit(APPEARANCE_SET_PREFERENCES_VIEW, true);
  } else {
    store.commit(APPEARANCE_SET_PREFERENCES_VIEW, false);
  }

  if (store.getters['authorisation/isLoggedIn']) {
    const permission = to.meta.permission ? to.meta.permission : 4;
    if (
      [
        'Settings',
        'Context',
        'ContentAdmin',
        'GroupAdmin',
        'AuditLog',
        'ReportDashboard',
        'AssuranceIntegration',
        'ContentRegister'
      ].includes(to.name)
    ) {
      // By defult the permission value is 2 for all Admin Dashboard. Check if the user role is minimmum 2
      // Note, the same logic is being checked to display the menu items in Appside Nav (like roleId is 4 or 3 or 2)
      if (store.getters['authorisation/isAllowedTo'](permission)) {
        next();
      } else {
        next('/');
      }
    } else if (
      to.name !== 'login' &&
      (permission >= 0 || store.getters['authorisation/isAllowedTo'](permission))
    ) {
      next();
    } else {
      next('/');
    }
  } else if (to.name === 'login' || to.name === 'LoginCallback' || to.name === 'login/callback') {
    next();
  } else if (to.name !== 'Page') {
    next('/login');
  } else {
    const isPagePublic = await store.dispatch('authorisation/isPagePublic', {
      moduleId: to.params.moduleId,
      pagePath: to.params.pagePath
    });

    if (isPagePublic) {
      // Allow public pages to go through
      store.commit(APPEARANCE_SET_IS_PUBLIC_VIEW_MODE, true);
      store.commit(AUTHORISATION_SET_VIEW_TYPE, VIEW_TYPES.CLIENT);
      store.dispatch('authorisation/saveUserRole', ROLES.CLIENT_USER);
      store.commit(types.APP_SET_INITIALISED);
      next();
    } else {
      localStorage.setItem('id_returnroute', window.location.pathname);
      next('/login');
    }
  }
};

export default router;

export { beforeEachRoute };
